import Web3 from 'web3';
import { ERC20_ABI } from '../../constants/wallet.constants';
import {
  formatChainConfigurationError,
  formatRateLimitError,
  formatUnsupportedChainMessage,
  getChainMinPriorityFeeWei,
  getEvmChain,
  getMetaMaskChainParams,
} from '../../constants/evmChain.registry';
import {
  coinNameMatchesAsset,
  formatShortWalletAddress,
  getWalletSettlement,
  isNativePairAsset,
  resolveEip712VerifyingContract,
  resolveSettlementAssetToken,
  resolveSettlementPaymentToken,
  resolvePairLegTokenCandidates,
  resolveLinkedWalletType,
  resolveNativeAccountAssetLabel,
  resolveWalletChainId,
  resolveWalletMetaMaskChainParams,
  resolveWalletNativeCurrencySymbol,
} from './linkedWallet.utils';
import {
  logNonCustodialBalance,
  errorNonCustodialBalance,
} from './nonCustodialBalance.debug';
import {
  isNonCustodialRpcBusy,
  setNonCustodialRpcBusy,
} from './nonCustodialSession';
import { callWithReadWeb3, invalidateReadCache, withReadCache } from './nonCustodialReadRpc';
import { decimalAmountToTokenUnits, multiplyHumanDecimals, toUint256String } from './tokenUnits.utils';

const ERC20_APPROVE_GAS_LIMIT = 100000;
const SEND_TX_MAX_ATTEMPTS = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRateLimitError = (error) => {
  const code = error?.code ?? error?.data?.code;
  const message = String(error?.message ?? '');
  return (
    code === -32005 ||
    code === -32603 ||
    /rate limit/i.test(message)
  );
};

const parseWeb3RpcError = (error, chainId) => {
  const code = error?.code ?? error?.data?.code;
  const message = String(error?.message ?? '');
  if (isRateLimitError(error)) {
    return new Error(formatRateLimitError(chainId));
  }
  if (code === 4001 || /user rejected|user denied/i.test(message)) {
    return new Error('Token approval was rejected in MetaMask.');
  }
  return error instanceof Error ? error : new Error(message || 'Wallet request failed.');
};

const toChecksum = (web3, address) => {
  if (!address) return '';
  try {
    return web3.utils.toChecksumAddress(String(address).trim());
  } catch {
    return String(address).trim().toLowerCase();
  }
};

const compareAddresses = (web3, left, right) => {
  if (!left || !right) return false;
  try {
    return (
      toChecksum(web3, left).toLowerCase() === toChecksum(web3, right).toLowerCase()
    );
  } catch {
    return String(left).toLowerCase() === String(right).toLowerCase();
  }
};

export const getMetaMaskProvider = () => {
  if (typeof window === 'undefined') return null;
  const { ethereum } = window;
  if (!ethereum) return null;
  if (Array.isArray(ethereum.providers)) {
    return ethereum.providers.find((provider) => provider.isMetaMask) ?? ethereum;
  }
  return ethereum.isMetaMask ? ethereum : null;
};

const syncMetaMaskChainRpc = async (provider, chainId, wallet) => {
  const chainParams =
    (wallet && resolveWalletMetaMaskChainParams(wallet)) ?? getMetaMaskChainParams(chainId);
  if (!chainParams?.rpcUrls?.length) return;

  try {
    await provider.request({
      method: 'wallet_updateEthereumChain',
      params: [{
        chainId: chainParams.chainId,
        chainName: chainParams.chainName,
        nativeCurrency: chainParams.nativeCurrency,
        rpcUrls: chainParams.rpcUrls,
        blockExplorerUrls: chainParams.blockExplorerUrls ?? [],
      }],
    });
    logNonCustodialBalance('metamask:rpc-updated', {
      chainId,
      rpcUrl: chainParams.rpcUrls[0],
      source: wallet?.rpcUrl ? 'api' : 'registry',
    });
  } catch (error) {
    logNonCustodialBalance('metamask:rpc-update-skipped', {
      chainId,
      message: error?.message,
    });
  }
};

export const connectMetaMask = async ({ expectedAddress, chainId, wallet } = {}) => {
  const provider = getMetaMaskProvider();
  if (!provider) {
    throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
  }

  const accounts = await provider.request({ method: 'eth_requestAccounts' });
  if (!Array.isArray(accounts) || !accounts.length) {
    throw new Error('No wallet account returned from MetaMask.');
  }

  if (chainId != null && Number.isFinite(Number(chainId))) {
    const numericChainId = Number(chainId);
    const targetChainId = `0x${numericChainId.toString(16)}`;
    const currentChainId = await provider.request({ method: 'eth_chainId' });
    if (String(currentChainId).toLowerCase() !== targetChainId.toLowerCase()) {
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: targetChainId }],
        });
      } catch (error) {
        if (error?.code === 4902) {
          const chainParams =
            (wallet && resolveWalletMetaMaskChainParams(wallet)) ??
            getMetaMaskChainParams(numericChainId);
          if (!chainParams) {
            throw new Error(formatUnsupportedChainMessage(numericChainId));
          }
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [chainParams],
          });
        } else {
          throw new Error(
            error?.message || 'Please switch MetaMask to the correct network for this wallet.',
          );
        }
      }
    }

    await syncMetaMaskChainRpc(provider, numericChainId, wallet);
  }

  const web3 = new Web3(provider);
  const normalizedExpected = expectedAddress
    ? String(expectedAddress).trim()
    : '';

  let connectedAddress = accounts[0];
  if (normalizedExpected) {
    const matchingAccount = accounts.find((account) =>
      compareAddresses(web3, account, normalizedExpected),
    );
    if (matchingAccount) {
      connectedAddress = matchingAccount;
    } else if (!compareAddresses(web3, connectedAddress, normalizedExpected)) {
      const mismatchError = new Error(
        `Connected MetaMask account (${formatShortWalletAddress(connectedAddress)}) does not match the linked wallet (${formatShortWalletAddress(normalizedExpected)}). Open MetaMask, select the linked account, then try again.`,
      );
      mismatchError.code = 'WALLET_ADDRESS_MISMATCH';
      mismatchError.connectedAddress = connectedAddress;
      mismatchError.expectedAddress = normalizedExpected;
      throw mismatchError;
    }
  }

  return {
    provider,
    web3,
    address: toChecksum(web3, connectedAddress),
  };
};

export const fetchNativeBalance = async (
  walletWeb3,
  address,
  chainIdHint,
  wallet,
  { useCache = false } = {},
) => {
  const chainId =
    chainIdHint != null && Number.isFinite(Number(chainIdHint))
      ? Number(chainIdHint)
      : await walletWeb3.eth.getChainId().catch(() => null);

  const cacheKey = `native:${chainId}:${String(address).toLowerCase()}`;

  return withReadCache(
    cacheKey,
    async () => {
    logNonCustodialBalance('fetchNativeBalance:start', { address, chainId });
    try {
      const balance = await callWithReadWeb3(
        walletWeb3,
        chainId,
        async (web3, meta) => {
        logNonCustodialBalance('fetchNativeBalance:request', {
          address,
          chainId,
          viaPublicRpc: meta.viaPublicRpc,
          rpcUrl: meta.rpcUrl,
        });
        const wei = await web3.eth.getBalance(address);
        return web3.utils.fromWei(wei, 'ether');
      },
        { wallet },
      );
      logNonCustodialBalance('fetchNativeBalance:success', { address, chainId, balance });
      return balance;
    } catch (error) {
      errorNonCustodialBalance('fetchNativeBalance:error', {
        address,
        message: error?.message,
      });
      throw error;
    }
  },
    { useCache },
  );
};

export const fetchErc20Balance = async (
  walletWeb3,
  { tokenAddress, ownerAddress, assetSymbol, wallet, useCache = false },
) => {
  const chainId = Number(wallet?.chainId);
  const cacheKey = `erc20:${chainId}:${tokenAddress?.toLowerCase()}:${ownerAddress?.toLowerCase()}`;

  return withReadCache(
    cacheKey,
    async () => {
    logNonCustodialBalance('fetchErc20Balance:start', {
      tokenAddress,
      ownerAddress,
      assetSymbol,
    });
    try {
      const balance = await callWithReadWeb3(
        walletWeb3,
        chainId,
        async (web3, meta) => {
        logNonCustodialBalance('fetchErc20Balance:request', {
          tokenAddress,
          ownerAddress,
          viaPublicRpc: meta.viaPublicRpc,
          rpcUrl: meta.rpcUrl,
        });
        const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
        const decimals = await resolveTokenDecimals(web3, {
          tokenAddress,
          assetSymbol,
          wallet,
        });
        const rawBalance = await contract.methods.balanceOf(ownerAddress).call();
        return Number(rawBalance) / 10 ** decimals;
      },
        { wallet },
      );
      logNonCustodialBalance('fetchErc20Balance:success', {
        tokenAddress,
        ownerAddress,
        assetSymbol,
        balance,
      });
      return balance;
    } catch (error) {
      const looksLikeSpenderMisconfig = isSettlementTokenAddress(tokenAddress, wallet);

      errorNonCustodialBalance('fetchErc20Balance:error', {
        tokenAddress,
        ownerAddress,
        message: error?.message,
        hint: looksLikeSpenderMisconfig
          ? 'Token address matches the settlement contract from linked-wallets API. Ask backend to set settlement.tokenAddress to the USDC ERC-20 contract.'
          : undefined,
      });
      throw error;
    }
  },
    { useCache },
  );
};

export const fetchErc20AllowanceRaw = async (
  walletWeb3,
  { tokenAddress, ownerAddress, spenderAddress, wallet },
) => {
  if (!tokenAddress || !ownerAddress || !spenderAddress) return '0';

  const chainId = Number(wallet?.chainId);
  const cacheKey = `allowanceRaw:${chainId}:${tokenAddress?.toLowerCase()}:${ownerAddress?.toLowerCase()}:${spenderAddress?.toLowerCase()}`;

  return withReadCache(cacheKey, async () => {
    const rawAllowance = await callWithReadWeb3(
      walletWeb3,
      chainId,
      async (web3) => {
      const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      return contract.methods.allowance(ownerAddress, spenderAddress).call();
    },
      { wallet },
    );

    return String(rawAllowance ?? '0');
  });
};

export const fetchErc20Allowance = async (
  walletWeb3,
  { tokenAddress, ownerAddress, spenderAddress, assetSymbol, wallet },
) => {
  if (!tokenAddress || !ownerAddress || !spenderAddress) return 0;

  const chainId = Number(wallet?.chainId);
  const cacheKey = `allowance:${chainId}:${tokenAddress?.toLowerCase()}:${ownerAddress?.toLowerCase()}:${spenderAddress?.toLowerCase()}`;

  return withReadCache(cacheKey, async () => {
    logNonCustodialBalance('fetchErc20Allowance:start', {
      tokenAddress,
      ownerAddress,
      spenderAddress,
    });

    const allowance = await callWithReadWeb3(
      walletWeb3,
      chainId,
      async (web3, meta) => {
      logNonCustodialBalance('fetchErc20Allowance:request', {
        tokenAddress,
        spenderAddress,
        viaPublicRpc: meta.viaPublicRpc,
        rpcUrl: meta.rpcUrl,
      });
      const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
      const decimals = await resolveTokenDecimals(web3, {
        tokenAddress,
        assetSymbol,
        wallet,
      });
      const rawAllowance = await contract.methods
        .allowance(ownerAddress, spenderAddress)
        .call();
      return Number(rawAllowance) / 10 ** decimals;
    },
      { wallet },
    );

    logNonCustodialBalance('fetchErc20Allowance:success', {
      tokenAddress,
      spenderAddress,
      allowance,
    });
    return allowance;
  });
};

export const compareUintStrings = (leftRaw, rightRaw) => {
  const left = String(leftRaw ?? '0').replace(/^0+/, '') || '0';
  const right = String(rightRaw ?? '0').replace(/^0+/, '') || '0';
  if (left.length !== right.length) {
    return left.length > right.length ? 1 : -1;
  }
  if (left === right) return 0;
  return left > right ? 1 : -1;
};

/** Raw uint256 comparison only — avoids false "sufficient" from float/human fallback. */
export const isAllowanceSufficientRaw = (currentRaw, requiredRaw) => {
  const required = String(requiredRaw ?? '').trim();
  if (!required || required === '0') return true;
  return compareUintStrings(currentRaw, required) >= 0;
};

/** Read allowance via the connected wallet RPC (same view MetaMask/backend use). */
export const readErc20AllowanceRawDirect = async (
  web3,
  { tokenAddress, ownerAddress, spenderAddress },
) => {
  if (!web3 || !tokenAddress || !ownerAddress || !spenderAddress) return '0';

  const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
  const rawAllowance = await contract.methods
    .allowance(toChecksum(web3, ownerAddress), toChecksum(web3, spenderAddress))
    .call();
  return String(rawAllowance ?? '0');
};

const ALLOWANCE_VERIFY_MAX_ATTEMPTS = 8;
const ALLOWANCE_VERIFY_DELAY_MS = 1500;

/** Prefer public RPC for allowance reads; fall back to the wallet provider. */
const readErc20AllowanceRawResolved = async (web3, allowanceParams, wallet) => {
  const chainId = resolveWalletChainId(wallet);
  return callWithReadWeb3(
    web3,
    chainId,
    async (readWeb3) =>
      readErc20AllowanceRawDirect(readWeb3, allowanceParams),
    { wallet },
  );
};

const waitForAllowanceAtLeast = async ({
  web3,
  allowanceParams,
  wallet,
  requiredAmountRaw,
  assetSymbol,
  transactionHash,
}) => {
  for (let attempt = 0; attempt < ALLOWANCE_VERIFY_MAX_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await sleep(ALLOWANCE_VERIFY_DELAY_MS);
    }

    invalidateReadCache();

    const currentAllowanceRaw = await readErc20AllowanceRawResolved(
      web3,
      allowanceParams,
      wallet,
    );

    if (isAllowanceSufficientRaw(currentAllowanceRaw, requiredAmountRaw)) {
      logNonCustodialBalance('ensureErc20Allowance:verified', {
        assetSymbol,
        attempt,
        currentAllowanceRaw,
        requiredAmountRaw,
        transactionHash,
      });
      return currentAllowanceRaw;
    }

    logNonCustodialBalance('ensureErc20Allowance:verify-pending', {
      assetSymbol,
      attempt,
      currentAllowanceRaw,
      requiredAmountRaw,
      transactionHash,
    });
  }

  return null;
};

const toTokenUnits = (amount, decimals) => decimalAmountToTokenUnits(amount, decimals);

const toWeiNumber = (value) => {
  const parsed = Number(String(value ?? '0'));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.trunc(parsed);
};

const toHexQuantity = (value) => {
  const normalized = toWeiNumber(value);
  return `0x${normalized.toString(16)}`;
};

const resolveMinPriorityFeeWei = (chainId) => {
  const configured = getChainMinPriorityFeeWei(chainId);
  return configured ? toWeiNumber(configured) : toWeiNumber('1500000000');
};

export const getEvmTransactionFeesForChain = (chainId) => {
  const minPriorityFeeWei = getChainMinPriorityFeeWei(chainId);
  if (!minPriorityFeeWei) return null;

  const maxPriorityFeePerGas = String(minPriorityFeeWei);
  const maxFeePerGas = String(toWeiNumber(minPriorityFeeWei) * 4);

  return { maxPriorityFeePerGas, maxFeePerGas };
};

export const resolveEvmTransactionFees = async (web3, chainIdHint) => {
  const chainId = Number.isFinite(Number(chainIdHint))
    ? Number(chainIdHint)
    : Number(await web3.eth.getChainId());
  const staticFees = getEvmTransactionFeesForChain(chainId);
  if (staticFees) return staticFees;

  const configuredMin = resolveMinPriorityFeeWei(chainId);

  try {
    const block = await web3.eth.getBlock('latest');
    const baseFeePerGas = toWeiNumber(block?.baseFeePerGas);

    if (baseFeePerGas > 0) {
      let maxPriorityFeePerGas = configuredMin;

      try {
        const networkGasPrice = toWeiNumber(await web3.eth.getGasPrice());
        const suggestedTip = Math.floor(networkGasPrice / 10);
        if (suggestedTip > maxPriorityFeePerGas) {
          maxPriorityFeePerGas = suggestedTip;
        }
      } catch {
        // keep configured minimum
      }

      const maxFeePerGas = baseFeePerGas * 2 + maxPriorityFeePerGas;

      return {
        maxPriorityFeePerGas: String(maxPriorityFeePerGas),
        maxFeePerGas: String(maxFeePerGas),
      };
    }
  } catch {
    // fall through to legacy gasPrice
  }

  let gasPrice = toWeiNumber(await web3.eth.getGasPrice());
  if (gasPrice < configuredMin) {
    gasPrice = configuredMin;
  } else {
    gasPrice = Math.floor((gasPrice * 12) / 10);
  }

  return { gasPrice: String(gasPrice) };
};

const resolveApprovalChainId = (wallet) => {
  const chainId = resolveWalletChainId(wallet);
  if (!Number.isFinite(chainId)) {
    throw new Error(formatChainConfigurationError());
  }
  return chainId;
};

const getWeb3Provider = (web3) => {
  const provider = getMetaMaskProvider() ?? web3?.currentProvider;
  if (!provider?.request) {
    throw new Error('Wallet provider is not available. Reconnect MetaMask and try again.');
  }
  return provider;
};

const sendWalletTransaction = async (web3, txParams, { onTxRequested, chainId } = {}) => {
  const provider = getWeb3Provider(web3);
  logNonCustodialBalance('sendWalletTransaction:request', txParams);

  onTxRequested?.();

  let lastError = null;
  for (let attempt = 0; attempt < SEND_TX_MAX_ATTEMPTS; attempt += 1) {
    try {
      const transactionHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });
      logNonCustodialBalance('sendWalletTransaction:submitted', {
        transactionHash,
        attempt,
      });
      return transactionHash;
    } catch (error) {
      lastError = error;
      if (!isRateLimitError(error) || attempt >= SEND_TX_MAX_ATTEMPTS - 1) {
        throw parseWeb3RpcError(error, chainId);
      }

      const delayMs = 8000 * (attempt + 1);
      logNonCustodialBalance('sendWalletTransaction:rate-limit-retry', {
        attempt: attempt + 1,
        delayMs,
      });
      await sleep(delayMs);
    }
  }

  throw parseWeb3RpcError(lastError, chainId);
};

export const approveErc20Allowance = async (
  web3,
  {
    tokenAddress,
    ownerAddress,
    spenderAddress,
    amount,
    amountRaw,
    assetSymbol,
    wallet,
    onApprovalTxRequested,
    allowZero = false,
  },
) => {
  const chainId = resolveApprovalChainId(wallet);
  const decimals = await resolveTokenDecimals(web3, {
    tokenAddress,
    assetSymbol,
    wallet,
  });
  const tokenSymbol = String(assetSymbol ?? 'TOKEN').trim().toUpperCase();
  const value = toUint256String(
    amountRaw != null && String(amountRaw).trim() !== ''
      ? String(amountRaw).trim()
      : toTokenUnits(amount, decimals),
  );

  logNonCustodialBalance('approveErc20Allowance:request', {
    tokenAddress,
    tokenSymbol,
    assetSymbol,
    decimals,
    humanAmount: amount,
    tokenUnits: value,
    spenderAddress,
    chainId,
  });

  if (value === '0' && !allowZero) {
    throw new Error(`Approval amount for ${tokenSymbol} must be greater than zero.`);
  }

  const feeParams =
    getEvmTransactionFeesForChain(chainId) ??
    (await resolveEvmTransactionFees(web3, chainId));
  if (!feeParams?.maxPriorityFeePerGas && !feeParams?.gasPrice) {
    throw new Error(formatUnsupportedChainMessage(chainId));
  }

  logNonCustodialBalance('approveErc20Allowance:fees', {
    gasLimit: ERC20_APPROVE_GAS_LIMIT,
    chainId,
    ...feeParams,
  });

  const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
  const data = contract.methods.approve(spenderAddress, value).encodeABI();
  const txParams = {
    from: toChecksum(web3, ownerAddress),
    to: toChecksum(web3, tokenAddress),
    data,
    gas: toHexQuantity(ERC20_APPROVE_GAS_LIMIT),
    ...(feeParams.maxPriorityFeePerGas
      ? {
          maxPriorityFeePerGas: toHexQuantity(feeParams.maxPriorityFeePerGas),
          maxFeePerGas: toHexQuantity(feeParams.maxFeePerGas),
        }
      : {
          gasPrice: toHexQuantity(feeParams.gasPrice),
        }),
  };

  const approvalMode =
    value === '0' ? 'revoke' : allowZero ? 'set' : 'approve';
  const approvalContext = {
    mode: approvalMode,
    amount:
      amount != null && String(amount).trim() !== ''
        ? amount
        : value === '0'
          ? 0
          : undefined,
    amountRaw: value,
    decimals,
    assetSymbol: tokenSymbol,
    tokenAddress,
    spenderAddress,
  };

  const transactionHash = await sendWalletTransaction(web3, txParams, {
    onTxRequested: () => onApprovalTxRequested?.(approvalContext),
    chainId,
  });
  return waitForTransactionReceipt(web3, transactionHash, chainId, wallet);
};

const isReceiptSuccessful = (receipt) => {
  const status = receipt?.status;
  if (status == null) return true;
  if (status === true || status === 1 || status === '1' || status === '0x1') return true;
  if (status === false || status === 0 || status === '0' || status === '0x0') return false;
  return String(status) !== '0';
};

export const waitForTransactionReceipt = async (
  walletWeb3,
  transactionHash,
  chainIdHint,
  wallet,
) => {
  logNonCustodialBalance('waitForTransactionReceipt:start', { transactionHash, chainIdHint });

  let receipt = null;
  for (let attempt = 0; attempt < 30; attempt += 1) {
    receipt = await callWithReadWeb3(
      walletWeb3,
      chainIdHint,
      async (web3) =>
        web3.eth.getTransactionReceipt(transactionHash).catch(() => null),
      { wallet },
    );
    if (receipt) break;
    const delayMs = attempt === 0 ? 1500 : 2000;
    await sleep(delayMs);
  }

  if (!receipt) {
    throw new Error('Transaction confirmation timed out. Please try again.');
  }

  logNonCustodialBalance('waitForTransactionReceipt:confirmed', {
    transactionHash,
    status: receipt?.status,
  });

  if (!isReceiptSuccessful(receipt)) {
    throw new Error('Token approval transaction failed.');
  }
  return receipt;
};

const normalizeAddressKey = (address) =>
  String(address ?? '')
    .trim()
    .toLowerCase();

const isSettlementTokenAddress = (address, wallet) => {
  const candidate = normalizeAddressKey(address);
  if (!candidate) return false;

  const settlement = getWalletSettlement(wallet);

  const spenderKeys = new Set(
    [
      wallet?.spenderAddress,
      settlement?.spenderAddress,
      settlement?.spender_address,
      settlement?.settlementContract,
      settlement?.settlement_contract,
      wallet?.raw?.spenderAddress,
      wallet?.raw?.spender_address,
      wallet?.raw?.settlementContract,
      wallet?.raw?.settlement_contract,
      wallet?.raw?.settlement?.spenderAddress,
      wallet?.raw?.settlement?.settlementContract,
    ]
      .filter(Boolean)
      .map(normalizeAddressKey),
  );

  return spenderKeys.has(candidate);
};

export const resolveTokenContractAddress = (wallet, assetSymbol) => {
  const key = String(assetSymbol ?? '').trim().toLowerCase();
  if (!key) return null;

  const contracts = wallet?.tokenContracts ?? {};
  const apiContract = contracts[key];
  if (apiContract && !isSettlementTokenAddress(apiContract, wallet)) {
    return String(apiContract).trim();
  }

  for (const [symbol, address] of Object.entries(contracts)) {
    if (!coinNameMatchesAsset(symbol, key)) continue;
    if (address && !isSettlementTokenAddress(address, wallet)) {
      return String(address).trim();
    }
  }

  return null;
};

export const resolveTokenDecimals = async (
  web3,
  { tokenAddress, assetSymbol, wallet },
) => {
  const symbolKey = String(assetSymbol ?? '').trim().toLowerCase();

  if (symbolKey.includes('usdc') || symbolKey.includes('usdt')) {
    return 6;
  }

  try {
    const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
    const decimalsRaw = await contract.methods.decimals().call();
    const parsed = Number(decimalsRaw);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  } catch {
    // use default below
  }

  return 18;
};

export const resolveTokenSymbol = async (web3, tokenAddress, assetSymbol) => {
  const fallback = String(assetSymbol ?? '').trim().toUpperCase() || 'TOKEN';
  try {
    const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
    const symbol = await contract.methods.symbol().call();
    const normalized = String(symbol ?? '').trim();
    return normalized || fallback;
  } catch {
    return fallback;
  }
};

export const buildNonCustodialPairBalance = async ({
  web3,
  wallet,
  selectedPair,
  ownerAddress,
  linkedWallets = [],
}) => {
  if (isNonCustodialRpcBusy()) {
    logNonCustodialBalance('buildPairBalance:skipped-rpc-busy', {
      pairKey: selectedPair?.pair_key,
      ownerAddress,
    });
    return null;
  }

  const baseCurrency = String(selectedPair?.baseCurrency ?? '').toLowerCase();
  const otherCurrency = String(selectedPair?.otherCurrency ?? '').toLowerCase();

  logNonCustodialBalance('buildPairBalance:start', {
    pairKey: selectedPair?.pair_key,
    baseCurrency,
    otherCurrency,
    ownerAddress,
    chainId: wallet?.chainId,
    tokenContracts: wallet?.tokenContracts ?? {},
  });

  const trimAddr = (value) => (value == null ? '' : String(value).trim());

  const resolveAssetBalance = async (assetSymbol, isNative = false) => {
    if (!assetSymbol) return null;
    let balance = 0;
    const assetKey = String(assetSymbol).trim().toLowerCase();

    const tokenCandidates = resolvePairLegTokenCandidates(
      wallet,
      linkedWallets,
      selectedPair,
      assetKey,
    );

    const treatAsNative =
      isNative ||
      (isNativePairAsset(assetSymbol) && !tokenCandidates.length);

    try {
      if (treatAsNative) {
        balance = Number(await fetchNativeBalance(web3, ownerAddress, wallet?.chainId, wallet));
      } else if (tokenCandidates.length) {
        let bestBalance = 0;
        let resolvedTokenAddress = tokenCandidates[0];

        for (const tokenAddress of tokenCandidates) {
          try {
            const read = Number(
              await fetchErc20Balance(web3, {
                tokenAddress,
                ownerAddress,
                assetSymbol,
                wallet,
              }),
            );
            if (Number.isFinite(read) && read >= bestBalance) {
              bestBalance = read;
              resolvedTokenAddress = tokenAddress;
            }
          } catch (candidateError) {
            errorNonCustodialBalance('buildPairBalance:candidate-failed', {
              assetSymbol,
              tokenAddress,
              chainId: wallet?.chainId,
              message: candidateError?.message,
            });
          }
        }

        balance = bestBalance;
        logNonCustodialBalance('buildPairBalance:asset-resolved', {
          assetSymbol,
          tokenAddress: resolvedTokenAddress,
          balance,
          candidateCount: tokenCandidates.length,
        });
      } else {
        logNonCustodialBalance('buildPairBalance:skip-no-contract', { assetSymbol });
        return {
          asset_name: assetSymbol,
          total_balance: 0,
          available_balance: 0,
        };
      }
    } catch (error) {
      errorNonCustodialBalance('buildPairBalance:asset-failed', {
        assetSymbol,
        tokenAddress: tokenCandidates[0],
        chainId: wallet?.chainId,
        message: error?.message,
      });
      return {
        asset_name: assetSymbol,
        total_balance: 0,
        available_balance: 0,
      };
    }

    if (!Number.isFinite(balance)) balance = 0;
    return {
      asset_name: assetSymbol,
      total_balance: balance,
      available_balance: balance,
    };
  };

  const pairLegKeys = new Set([baseCurrency, otherCurrency]);
  const pairBalances = await Promise.all([
    resolveAssetBalance(baseCurrency, false),
    resolveAssetBalance(otherCurrency, false),
  ]);

  const nativeLabel = resolveNativeAccountAssetLabel(wallet, linkedWallets);
  const nativeRow = pairLegKeys.has(nativeLabel)
    ? null
    : await resolveAssetBalance(nativeLabel, true);

  const assets = [...pairBalances.filter(Boolean)];
  if (nativeRow) assets.push(nativeRow);

  const walletType = (
    resolveLinkedWalletType(wallet) ?? 'METAMASK'
  ).toUpperCase();
  const walletId = wallet?.walletId ?? wallet?.wallet_id ?? null;

  const enrichedAssets = assets.map((row) => ({
    ...row,
    address: ownerAddress,
    wallet_id: walletId,
    wallet_type: walletType,
  }));

  const totalBalance = enrichedAssets
    .filter((row) =>
      pairLegKeys.has(String(row.asset_name ?? '').toLowerCase()),
    )
    .reduce((sum, row) => sum + (Number(row.total_balance) || 0), 0);

  const result = {
    wallet: enrichedAssets,
    totalUsdBalance: totalBalance,
    availableUsdBalance: totalBalance,
  };

  logNonCustodialBalance('buildPairBalance:complete', result);
  return result;
};

export const resolveSpenderAddress = (wallet, selectedPair) => {
  const fromEip712 = resolveEip712VerifyingContract(wallet);
  if (fromEip712) return fromEip712;

  const settlement = getWalletSettlement(wallet);
  const candidates = [
    wallet?.spenderAddress,
    settlement?.spenderAddress,
    settlement?.spender_address,
    settlement?.settlementContract,
    settlement?.settlement_contract,
    wallet?.raw?.spenderAddress,
    wallet?.raw?.spender_address,
    wallet?.raw?.approvalSpender,
    wallet?.raw?.approval_spender,
    wallet?.raw?.settlementContract,
    wallet?.raw?.settlement_contract,
    wallet?.raw?.settlementAddress,
    wallet?.raw?.settlement_address,
    wallet?.raw?.settlement?.spenderAddress,
    wallet?.raw?.settlement?.settlementContract,
    selectedPair?.spenderAddress,
    selectedPair?.spender_address,
    selectedPair?.settlementContract,
    selectedPair?.settlement_contract,
    selectedPair?.settlementAddress,
    selectedPair?.settlement_address,
  ];

  for (const candidate of candidates) {
    if (candidate) return String(candidate).trim();
  }

  return null;
};

export const getOrderAllowanceBlockReason = ({
  selectedSide,
  selectedPair,
  wallet,
  quantity,
  price,
}) => {
  const qty = Number(quantity);
  const px = Number(price);
  if (!(qty > 0) || !(px > 0) || !selectedPair || !wallet) return null;

  const assetSymbol =
    selectedSide === 0 ? selectedPair.baseCurrency : selectedPair.otherCurrency;
  const tokenAddress = resolveTokenContractAddress(wallet, assetSymbol);
  if (!tokenAddress) return null;

  if (!resolveSpenderAddress(wallet, selectedPair)) {
    return 'Token approval requires settlement.spenderAddress from the linked-wallets API.';
  }

  return null;
};

export const resolveOrderAllowanceRequirement = ({
  selectedSide,
  selectedPair,
  wallet,
  quantity,
  price,
}) => {
  const qtyStr = String(quantity ?? '').trim();
  const pxStr = String(price ?? '').trim();
  const qty = Number(qtyStr);
  const px = Number(pxStr);
  if (!(qty > 0) || !(px > 0) || !selectedPair) {
    return null;
  }

  const spenderAddress = resolveSpenderAddress(wallet, selectedPair);
  if (!spenderAddress) return null;

  if (selectedSide === 0) {
    const assetSymbol = selectedPair.baseCurrency;
    const tokenAddress = resolveTokenContractAddress(wallet, assetSymbol);
    if (!tokenAddress) return null;
    const paymentDecimals =
      String(assetSymbol).includes('usdc') || String(assetSymbol).includes('usdt') ? 6 : 18;
    const paymentTotal = multiplyHumanDecimals(qtyStr, pxStr, paymentDecimals);
    return {
      assetSymbol,
      tokenAddress,
      requiredAmount: paymentTotal,
      requiredAmountRaw: toUint256String(
        decimalAmountToTokenUnits(paymentTotal, paymentDecimals),
      ),
      spenderAddress,
      wallet,
    };
  }

  const assetSymbol = selectedPair.otherCurrency;
  const tokenAddress = resolveTokenContractAddress(wallet, assetSymbol);
  if (!tokenAddress) return null;
  const assetDecimals =
    String(assetSymbol).includes('usdc') || String(assetSymbol).includes('usdt') ? 6 : 18;
  return {
    assetSymbol,
    tokenAddress,
    requiredAmount: qtyStr,
    requiredAmountRaw: toUint256String(decimalAmountToTokenUnits(qtyStr, assetDecimals)),
    spenderAddress,
    wallet,
  };
};

export const ensureErc20Allowance = async ({
  web3,
  ownerAddress,
  requirement,
  onApprovalTxRequested,
}) => {
  if (!requirement) return { approved: true, skipped: true };

  const requiredAmountRaw = toUint256String(requirement.requiredAmountRaw);
  if (!requiredAmountRaw || requiredAmountRaw === '0') {
    throw new Error('Token approval amount must be greater than zero.');
  }

  const allowanceParams = {
    tokenAddress: requirement.tokenAddress,
    ownerAddress,
    spenderAddress: requirement.spenderAddress,
  };

  setNonCustodialRpcBusy(true);
  try {
    invalidateReadCache();

    const wallet = requirement.wallet;
    const readCurrentAllowanceRaw = () =>
      readErc20AllowanceRawResolved(web3, allowanceParams, wallet);

    let currentAllowanceRaw = await readCurrentAllowanceRaw();

    if (isAllowanceSufficientRaw(currentAllowanceRaw, requiredAmountRaw)) {
      logNonCustodialBalance('ensureErc20Allowance:already-sufficient', {
        assetSymbol: requirement.assetSymbol,
        tokenAddress: requirement.tokenAddress,
        spenderAddress: requirement.spenderAddress,
        currentAllowanceRaw,
        requiredAmount: requirement.requiredAmount,
        requiredAmountRaw,
      });
      return { approved: true, skipped: false, currentAllowanceRaw };
    }

    logNonCustodialBalance('ensureErc20Allowance:request-approval', {
      assetSymbol: requirement.assetSymbol,
      tokenAddress: requirement.tokenAddress,
      spenderAddress: requirement.spenderAddress,
      currentAllowanceRaw,
      requiredAmount: requirement.requiredAmount,
      requiredAmountRaw,
    });

    const receipt = await approveErc20Allowance(web3, {
      tokenAddress: requirement.tokenAddress,
      ownerAddress,
      spenderAddress: requirement.spenderAddress,
      amount: requirement.requiredAmount,
      amountRaw: requiredAmountRaw,
      assetSymbol: requirement.assetSymbol,
      wallet: requirement.wallet,
      onApprovalTxRequested,
    });

    if (!isReceiptSuccessful(receipt)) {
      throw new Error('Token approval transaction failed.');
    }

    currentAllowanceRaw = await waitForAllowanceAtLeast({
      web3,
      allowanceParams,
      wallet,
      requiredAmountRaw,
      assetSymbol: requirement.assetSymbol,
      transactionHash: receipt?.transactionHash,
    });

    if (!currentAllowanceRaw) {
      throw new Error(
        `Token approval confirmed but allowance is still below the required amount for ${String(requirement.assetSymbol ?? 'token').toUpperCase()}. Please try again.`,
      );
    }

    return {
      approved: true,
      skipped: false,
      transactionHash: receipt?.transactionHash,
      currentAllowanceRaw,
    };
  } finally {
    setNonCustodialRpcBusy(false);
  }
};
